import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import "./style.scss";
import GoBack from "../../../assets/GoBackApp.svg";
import ChevronDownLiner from "../../../assets/ChevronDownLiner.svg";
import { useNavigate, useParams } from "react-router-dom";
import Button from "shared/ui/button";
import { PriceTypeModal } from "../components/price-type";
import {
  AssetType,
  OperationType,
  OrderTifType,
  OrderType,
} from "static/types";
import { dashboardStore } from "entities/dashboard";
import { useStore } from "zustand";
import { isQuantityOffStep, marketStore } from "entities/market";
import {
  formatPrice,
  getCommission,
  getFromLotsText,
  navigateToAsset,
} from "helpers/functions";
import { formatMoney } from "helpers/money";
import Loading from "widgets/loading";
import Modal from "widgets/auth/modal";
import { useTranslation } from "react-i18next";
import { ResponseCodes } from "shared/api/sircap";
import { toast } from "react-toastify";
import QuantityInput from "../components/quantity-input";
import useCanAction from "hooks/useCanAction";
import useExchangeTimeFrame, {
  ExchangeStatus,
} from "hooks/useExchangeTimeFrame";
import { AMX_EXCHANGE_ID, OrderTypes } from "static/data";
import BargainingAlert from "../components/alert";
import usePrice from "hooks/usePrice";
import MinusStep from "../../../assets/MinusStep.svg";
import PlusStep from "../../../assets/PlusStep.svg";
import ArrowSwitch from "../../../assets/ArrowSwitch.svg";
import Max from "../../../assets/MaxStep.svg";
import Info from "../../../assets/TryMyPriceInfo.svg";
import { fbLogEventOnce, FB_EVENTS } from "utils/fbEvents";
import { sessionStore } from "entities/session/model/sessionModel";

export const SellPage = () => {
  const { t, i18n } = useTranslation("app");
  const { t: tError } = useTranslation("errors");
  const navigate = useNavigate();
  const [isActive, setIsActive] = useState(false);
  const [modal, setModal] = useState(false);
  const [checkedIndex, setCheckedIndex] = useState<number>(0);
  const [specificPrice, setSpecificPrice] = useState<number>();
  const [inputValue, setInputValue] = useState<number>(0);
  const [type, setType] = useState("");
  const { asset_id } = useParams();
  const {
    currencies,
    user_deposit_accounts,
    user_brokerage_accounts,
    loadUserAccounts,
  } = useStore(dashboardStore);
  const {
    isLoading,
    isInitialLoading,
    assetInfo,
    loadAssetInfo,
    openOrder,
    calculateBondPrice,
  } = useStore(marketStore);
  const symbol = assetInfo?.asset?.symbol || "";
  const currSymbol = assetInfo?.asset?.currency?.symbol || "";
  const currCode = assetInfo?.asset?.currency?.code || "";
  const isBond = [AssetType.Bond, AssetType.EuroBond].includes(+type);
  const isFractional = assetInfo?.decimal_places ? true : false;
  const isCurrency = assetInfo?.asset?.type === AssetType.Currency;
  const isOption = +type === AssetType.Option;
  const [confirmDeal, setConfirmDeal] = useState(false);
  const action = useCanAction();
  const exchange = useExchangeTimeFrame(assetInfo?.asset?.exchange);
  const minQty = assetInfo?.asset?.minimal_quantity || 1;
  const lotSize = assetInfo?.asset?.lot_size || 1;
  const qty = inputValue ? +inputValue * lotSize : 0;
  const step = assetInfo?.step || 1;

  const isStock = [AssetType.Stock, AssetType.ETF, AssetType.Fond].includes(
    +type,
  );

  const isExchangeActive = exchange ? !!exchange?.isActive : true;
  const onlyForLimitOrders =
    [AssetType.Stock, AssetType.ETF, AssetType.Fond].includes(+type) && exchange
      ? [ExchangeStatus.PreMarket, ExchangeStatus.PostMarket].includes(
          exchange.status,
        )
      : false;

  const availableOrderTypes = assetInfo?.available_order_types;
  const availableOrderTifs = assetInfo?.available_tif_types || [];
  const orderTypes = useMemo(
    () =>
      OrderTypes(
        OperationType.Sell,
        isCurrency,
        onlyForLimitOrders,
        availableOrderTypes,
      ),
    [isCurrency, onlyForLimitOrders, availableOrderTypes],
  );
  const activeOrderType = useMemo(() => {
    if (!orderTypes.length) return undefined;
    const idx = Math.min(Math.max(0, checkedIndex), orderTypes.length - 1);
    return orderTypes[idx];
  }, [orderTypes, checkedIndex]);
  const isSpecific = activeOrderType?.showInput ?? false;
  const withSirius = activeOrderType?.with_sirius ?? false;
  const [load, setLoad] = useState(false);
  const [dealError, setDealError] = useState(false);

  const [tifModal, setTifModal] = useState(false);
  const [checkedTifIndex, setCheckedTifIndex] = useState<number>(0);
  const [tif, setTif] = useState<OrderTifType>();

  const [yieldInputFocused, setYieldInputFocused] = useState(false);
  const [yieldInputValue, setYieldInputValue] = useState("");
  const [commissionExpanded, setCommissionExpanded] = useState(true);
  const [calculatedBondYield, setCalculatedBondYield] = useState<number | null>(
    null,
  );
  const [isBondPriceCalculating, setIsBondPriceCalculating] = useState(false);
  const bondPriceCalcTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const bondYieldCalcTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const bondInfo = assetInfo?.bondInfo;
  const bondIsin = assetInfo?.asset?.isin;
  const isAmxAsset = assetInfo?.asset?.exchange?.id === AMX_EXCHANGE_ID;
  // Живёт здесь, а не в эффекте: им пользуются два эффекта ниже, и он должен
  // существовать до первого запуска любого из них.
  const orderTypeDefaultedRef = useRef(false);
  const onlyMarket =
    orderTypes.length === 1 && orderTypes[0]?.type === OrderType.Market;

  useEffect(() => {
    const urlObject = new URL(window.location.href);
    const limitOrderPrice = urlObject.searchParams.get("price");
    const limitOrderYield = urlObject.searchParams.get("yield");

    if (limitOrderPrice !== null) {
      setSpecificPrice(+limitOrderPrice);
      const limitIdx = orderTypes.findIndex((ot) => ot.showInput);
      setCheckedIndex(limitIdx >= 0 ? limitIdx : 0);

      // Доходность уровня стакана относится именно к подставленной цене.
      // Без неё в поле осталась бы рыночная `bid_yield`, которая цене из
      // стакана не соответствует.
      const parsedYield =
        limitOrderYield !== null && limitOrderYield !== ""
          ? +limitOrderYield
          : NaN;
      if (!Number.isNaN(parsedYield)) {
        setCalculatedBondYield(parsedYield);
      }
    } else if (!orderTypeDefaultedRef.current) {
      setCheckedIndex(0);
    }
  }, [orderTypes]);

  /**
   * Цена могла прийти из стакана без доходности — например, у уровня её не
   * было. Тогда считаем доходность по цене на сервере: показать рыночную
   * рядом с лимитной ценой хуже, чем показать прочерк.
   */
  const urlYieldResolvedRef = useRef(false);
  useEffect(() => {
    if (urlYieldResolvedRef.current) return;
    if (!isBond || !bondIsin) return;

    const urlObject = new URL(window.location.href);
    const priceParam = urlObject.searchParams.get("price");
    const yieldParam = urlObject.searchParams.get("yield");
    if (priceParam === null) return;
    if (yieldParam !== null && yieldParam !== "") {
      urlYieldResolvedRef.current = true;
      return;
    }

    const price = +priceParam;
    if (Number.isNaN(price) || price <= 0) return;

    urlYieldResolvedRef.current = true;
    setIsBondPriceCalculating(true);
    calculateBondPrice({ isin: bondIsin, price, yld: null })
      .then((result) => setCalculatedBondYield(result?.yld ?? null))
      .catch(() => setCalculatedBondYield(null))
      .finally(() => setIsBondPriceCalculating(false));
  }, [isBond, bondIsin]);

  const urlObject = useMemo(() => {
    try {
      return new URL(window.location.href);
    } catch {
      return null;
    }
  }, []);
  const optionStrike = urlObject?.searchParams.get("strike");
  const optionExpiration = urlObject?.searchParams.get("expiration");
  const optionTypeParam = urlObject?.searchParams.get("option_type");
  const optionTypeLabel =
    optionTypeParam === "1" ? "Call" : optionTypeParam === "2" ? "Put" : null;
  const optionContractId = urlObject?.searchParams.get("contract_id");

  useEffect(() => {
    try {
      const urlObject = new URL(window.location.href);
      const assetType = urlObject.searchParams.get("type")!;

      setType(assetType);

      loadAssetInfo(asset_id);
      loadUserAccounts();
    } catch (error) {
      navigate(-1);
    }
  }, []);

  const { bid, last, change, changePercent, bid_yield } = usePrice({
    exchange: exchange?.exchange_code,
    exchangeStatus: exchange?.status,
    symbol: symbol,
    assetType: type as unknown as AssetType,
    ask: assetInfo?.asset?.ask,
    bid: assetInfo?.asset?.bid,
    ask_yield: assetInfo?.asset?.data?.ask_yield || null,
    bid_yield: assetInfo?.asset?.data?.bid_yield || null,
    price: assetInfo?.asset?.price,
    change: assetInfo?.asset?.change,
    useAssetDepth: true,
    parentSymbol: symbol,
    strike: optionStrike ? +optionStrike : null,
    expiration: optionExpiration,
    optionType: optionTypeParam ? +optionTypeParam : null,
  });

  const getUserAccount = useCallback(() => {
    if (isOption) {
      return {
        symbol: currSymbol,
        amount: "—",
        totalAmount: 9999,
      };
    }
    if (assetInfo && assetInfo.asset) {
      if (isCurrency) {
        const acc = user_deposit_accounts.find(
          (acc) => acc.currency.code === symbol,
        );
        return {
          symbol: currencies.find((c) => c.code === symbol)?.symbol || "",
          amount: formatMoney(acc?.amount || 0),
          totalAmount: formatMoney(acc?.amount || 0),
        };
      } else {
        const acc = user_brokerage_accounts.find(
          (acc) => acc.asset.symbol === symbol,
        );
        const qty = acc?.quantity || 0;
        return {
          symbol: currencies.find((c) => c.code === symbol)?.symbol || "",
          amount: `${qty} ${symbol}`,
          totalAmount: qty,
        };
      }
    }
    return {
      symbol: currencies.find((c) => c.code === symbol)?.symbol || "",
      amount: 0,
      totalAmount: 0,
    };
  }, [
    user_deposit_accounts,
    user_brokerage_accounts,
    assetInfo,
    currencies,
    isCurrency,
    isOption,
  ]);

  const getAssetPrice = useCallback((): number => {
    try {
      if (isSpecific) return +(specificPrice || 0);
      // По AMX фолбэка на цену последней сделки нет: продажа по рынку требует
      // живого спроса. `bid` здесь уже собран из сокета и стакана, и ноль в
      // нём означает, что покупать некому — такую заявку считать не по чему,
      // а `last` может быть сколь угодно старым.
      if (isAmxAsset) return bid || 0;
      return bid || last;
    } catch (error) {
      return 0;
    }
  }, [isSpecific, specificPrice, assetInfo, bid, last]);

  const getTotalPrice = useCallback(
    (forOneLot = false, forOneUnit = false, withoutAI = false) => {
      try {
        const price = getAssetPrice();
        const qty = forOneLot ? 1 : +(inputValue || 0);
        const lot = forOneUnit ? 1 : lotSize;
        let total = price * qty * lot;
        if (isBond) {
          const { ai, nominal } = assetInfo!.bondInfo!;
          total = ((price / 100) * nominal + (withoutAI ? 0 : ai)) * qty * lot;
        }
        if (!total) return 0;
        return parseFloat(total.toFixed(isCurrency ? 6 : 4));
      } catch (error) {
        return 0;
      }
    },
    [isCurrency, inputValue, assetInfo, getAssetPrice, lotSize],
  );

  const getAvailableLots = useCallback(() => {
    if (isOption) return 9999;
    try {
      const acc = getUserAccount();
      const lots = isCurrency
        ? +(acc.totalAmount / lotSize).toFixed(4)
        : +(acc.totalAmount / lotSize).toFixed(2);
      if (lots === Infinity) return 0;
      if (!lots) return 0;
      return +lots;
    } catch (error) {
      return 0;
    }
  }, [getUserAccount, isOption, lotSize]);

  const getMinimumLots = useCallback(() => {
    try {
      if (isCurrency) return 1;

      if (isBond && isFractional) {
        return 0.01;
      }

      if (minQty && lotSize) {
        if (minQty === 1 && lotSize === 1 && isFractional) {
          return 0.01;
        } else {
          return Math.ceil(minQty / lotSize);
        }
      }

      return 1;
    } catch (error) {
      return 1;
    }
  }, [isCurrency, isBond, isFractional, minQty, lotSize]);

  const getCommissionWrap = useCallback(() => {
    try {
      return getCommission(
        assetInfo?.commission || 0,
        getTotalPrice(),
        OperationType.Sell,
        assetInfo?.minimal_commission || 0,
        withSirius,
        0,
        assetInfo?.min_commission_currency,
        assetInfo?.asset?.currency?.code,
        assetInfo?.min_commission_cb_rate,
      );
    } catch (error) {
      return [0, 0, 0];
    }
  }, [assetInfo, getTotalPrice, withSirius]);

  const getTotalPriceAndCommission = (): string => {
    try {
      const total = getTotalPrice();
      if (!total) return "";
      const commission = getCommissionWrap()[2];
      return `${formatPrice(total + commission, 4)}${currSymbol}`;
    } catch (error) {
      return "";
    }
  };

  const getAssetCommission = () => {
    try {
      const total = getTotalPrice();
      if (!total) return <></>;

      const [commission, additional, amount] = getCommissionWrap();

      const minCurrency = assetInfo?.min_commission_currency;
      const assetCurrencyCode = assetInfo?.asset?.currency?.code;
      const isCrossCurrencyMin =
        !!minCurrency &&
        !!assetCurrencyCode &&
        minCurrency !== assetCurrencyCode;

      if (isCrossCurrencyMin) {
        const cbRate = assetInfo?.min_commission_cb_rate ?? null;
        const minInMin = assetInfo!.minimal_commission;
        const finalInAsset = Math.abs(amount);
        const finalInMin = cbRate
          ? +(finalInAsset * cbRate).toFixed(2)
          : minInMin;
        if (!finalInAsset && !finalInMin) return <></>;
        const percentApplied = finalInMin > minInMin + 0.01;
        const rateDisplay =
          cbRate && cbRate >= 1
            ? `1 ${assetCurrencyCode} = ${cbRate} ${minCurrency}`
            : cbRate
            ? `1 ${minCurrency} = ${+(1 / cbRate).toFixed(
                4,
              )} ${assetCurrencyCode}`
            : null;
        return (
          <>
            <li>
              {t("commission").toLowerCase()}:{" "}
              <b>
                {finalInAsset} {assetCurrencyCode}
              </b>
              {rateDisplay && (
                <span style={{ opacity: 0.65, fontSize: "0.85em" }}>
                  {" "}
                  ({percentApplied ? `${commission}%, ` : ""}
                  {finalInMin} {minCurrency}, {rateDisplay})
                </span>
              )}
            </li>
          </>
        );
      }

      if (!amount) return <></>;

      const additionalText = additional
        ? `+ ${additional}${currSymbol}`
        : `${amount}${currSymbol}`;

      return (
        <>
          {amount > assetInfo!.minimal_commission ? (
            <li>
              {t("commission").toLowerCase()}:{" "}
              <b>
                {amount}
                {currSymbol} ({commission}%)
              </b>
            </li>
          ) : (
            <>
              <li>
                {t("commission").toLowerCase()}: <b>{additionalText}</b>
              </li>
              {!!additional && (
                <span>
                  {amount}
                  {currSymbol}
                </span>
              )}
            </>
          )}
        </>
      );
    } catch (error) {
      return <></>;
    }
  };

  const getPriceFormatted = useCallback(() => {
    try {
      if (isBond) {
        return `${bid_yield}% | ${bid}`;
      } else {
        return `${bid || last}${currSymbol}`;
      }
    } catch (error) {
      return "";
    }
  }, [bid, last, bid_yield]);

  const handleSell = (pinCode: string) => {
    if (assetInfo?.asset) {
      setLoad(true);
      openOrder(
        assetInfo.asset.id,
        {
          pinCode,
          comments: "",
          asset: assetInfo.asset.id,
          type: isSpecific ? OrderType.Limit : OrderType.Market,
          price: getTotalPrice(true, true, true),
          quantity: +(inputValue || 0) * lotSize,
          operation: OperationType.Sell,
          with_sirius: withSirius,
          tif: isStock && isSpecific ? tif : undefined,
          option_contract_id: optionContractId ? +optionContractId : undefined,
        },
        tError,
        (error?: number) => {
          setLoad(false);
          if (error) {
            if (error === ResponseCodes.WrongPinCode) {
              setDealError(true);
            } else {
              const err = String(error);
              toast.error(tError(err));
            }
          } else {
            toast.success("Success");
            void fbLogEventOnce(
              `first_trade_${sessionStore.getState().user?.id}`,
              FB_EVENTS.firstTrade,
            );
            navigateToAsset(assetInfo!.asset!.type, asset_id, navigate);
          }
        },
      );
    }
  };

  const isWrongBondQuantity = useMemo(
    () =>
      isQuantityOffStep({
        quantity: inputValue,
        step: assetInfo?.step,
        isFractional,
      }),
    [inputValue, assetInfo?.step, isFractional],
  );

  const isWrongBondQuantityContent = useMemo(() => {
    if (!isWrongBondQuantity) return <></>;
    return isWrongBondQuantity && inputValue >= 0 ? (
      <span
        className="quantity-validation-alert"
        dangerouslySetInnerHTML={{
          __html: t("purchaseInMultiplesOf1000Only", { step }),
        }}
      />
    ) : isWrongBondQuantity && inputValue < 0 ? (
      <span className="quantity-validation-alert">{t("wrongQuantity")}</span>
    ) : (
      <></>
    );
  }, [isWrongBondQuantity, inputValue, step, t]);

  const isWrongSpecificPrice = useMemo(() => {
    return (isSpecific && !specificPrice) || +(specificPrice || 0) < 0;
  }, [isSpecific, specificPrice]);

  const unavailable = useCallback(() => {
    if (isOption) {
      return (
        !inputValue ||
        inputValue < 0 ||
        inputValue < getMinimumLots() ||
        isWrongSpecificPrice
      );
    }
    const acc = getUserAccount();
    const minLots = getMinimumLots();
    const maxLots = getAvailableLots();
    const userBalance = acc.amount as any;
    if (
      !userBalance ||
      !inputValue ||
      inputValue < 0 ||
      inputValue < minLots ||
      inputValue > maxLots ||
      isWrongBondQuantity ||
      isWrongSpecificPrice
    )
      return true;
    if (isCurrency) {
      return inputValue > userBalance;
    } else {
      return inputValue > parseFloat(userBalance as string);
    }
  }, [
    isOption,
    inputValue,
    isCurrency,
    getUserAccount,
    getMinimumLots,
    getAvailableLots,
    isWrongBondQuantity,
    isWrongSpecificPrice,
  ]);

  const getDepthValidationAlert = () => {
    const isStockETF = [AssetType.ETF, AssetType.Stock].includes(
      assetInfo?.asset?.type as AssetType,
    );
    const isCurrency = assetInfo?.asset?.type === AssetType.Currency;
    const isAmx = assetInfo?.asset?.exchange?.id === 25;

    if (
      !isAmx &&
      isSpecific &&
      +specificPrice! > 0 &&
      (isCurrency || isStockETF)
    ) {
      if (isStockETF) {
        const bid85 = +((bid || last) * 0.85).toFixed(4);
        const bid115 = +((bid || last) * 1.15).toFixed(4);
        if (+specificPrice! >= bid85 && +specificPrice! <= bid115) {
          return;
        } else {
          return (
            <span className="depth-validation-alert">
              {t("priceMustBeWithinTheRangeETF")} {bid85} / {bid115}
            </span>
          );
        }
      } else {
        if (
          +specificPrice! >= assetInfo!.asset!.bid &&
          +specificPrice! <= assetInfo!.asset!.ask
        ) {
          return;
        } else {
          return (
            <span className="depth-validation-alert">
              {t("priceMustBeWithinTheRange")} {assetInfo!.asset!.bid} /{" "}
              {assetInfo!.asset!.ask}
            </span>
          );
        }
      }
    }

    return;
  };

  const showTif = isStock && isSpecific && !!availableOrderTifs.length;

  const tifOptions = useMemo(() => {
    return availableOrderTifs.map((item) => ({
      title: t(`${item}_title`),
      info: t(`${item}_subtitle`),
    }));
  }, [availableOrderTifs]);

  useEffect(() => {
    setCalculatedBondYield(null);
    setIsBondPriceCalculating(false);
  }, [asset_id]);

  /**
   * Для инструментов AMX тип ордера по умолчанию — лимитный, а цена берётся
   * из рынка ОДИН РАЗ и дальше не обновляется: пользователь должен подавать
   * заявку по той цене, которую увидел, а не по уехавшей за время раздумий.
   *
   * Срабатывает единожды и только при заходе «с нуля» (без цены в URL —
   * там уровень стакана уже всё выставил). Для остальных бирж остаётся
   * маркет, как было.
   */
  useEffect(() => {
    if (orderTypeDefaultedRef.current) return;
    if (!isAmxAsset || isCurrency) return;

    // Цена из стакана — свой путь, он выше.
    if (new URL(window.location.href).searchParams.get("price") !== null) {
      orderTypeDefaultedRef.current = true;
      return;
    }

    const limitIdx = orderTypes.findIndex((ot) => ot.showInput);
    // Лимит недоступен для инструмента — остаёмся на том, что есть.
    if (limitIdx < 0) return;

    // Котировка приходит асинхронно: пока цены нет, ждём следующего тика,
    // иначе выставили бы лимит с пустым полем цены.
    const marketPrice = bid ?? last;
    if (marketPrice == null || !(+marketPrice > 0)) return;

    orderTypeDefaultedRef.current = true;
    setCheckedIndex(limitIdx);
    setSpecificPrice(+marketPrice);
    // Цена равна рыночному bid, значит рыночная доходность к ней и относится.
    if (isBond && bid_yield != null) {
      setCalculatedBondYield(+bid_yield);
    }
  }, [isAmxAsset, isCurrency, orderTypes, bid, last, isBond, bid_yield]);

  const bondYieldDisplay = useMemo(() => {
    if (!isBond || !bondInfo) return bid_yield ?? null;
    if (isSpecific && calculatedBondYield != null) return calculatedBondYield;
    if (isSpecific && specificPrice != null && +specificPrice > 0) {
      const marketAsk = bid ?? last;
      if (
        marketAsk != null &&
        typeof marketAsk === "number" &&
        Math.abs(+specificPrice - marketAsk) < 0.01
      ) {
        return bid_yield ?? null;
      }
    }
    return bid_yield ?? null;
  }, [
    isBond,
    bondInfo,
    isSpecific,
    specificPrice,
    calculatedBondYield,
    bid_yield,
    bid,
    last,
  ]);

  const handleDecreaseQuantity = () => {
    if (inputValue > getMinimumLots()) {
      if (inputValue > getAvailableLots()) {
        setInputValue(getAvailableLots());
      } else {
        setInputValue(inputValue - step);
      }
    } else {
      return;
    }
  };

  const handleIncreaseQuantity = () => {
    if (!inputValue) {
      setInputValue(getMinimumLots());
    } else if (inputValue < getAvailableLots()) {
      if (inputValue < getMinimumLots()) {
        setInputValue(getMinimumLots());
      } else {
        setInputValue(inputValue + step);
      }
    } else {
      return;
    }
  };

  if (isInitialLoading || !assetInfo) return <Loading />;

  return (
    <div className="deal-container">
      <div className="deal-header">
        <img
          className="go-back-portfolio"
          onClick={() => navigate(-1)}
          src={GoBack}
          alt="Go Back"
        />
        <p>{+type === AssetType.Option ? t("short") : t("sell")}</p>
      </div>

      <div className="deal-main">
        <div className="deal-main_about-asset">
          <p>{symbol}</p>

          {isBond && bondInfo && (
            <span style={{ marginBottom: "-6px" }}>
              {t("nominal")}: {formatPrice(bondInfo.nominal, 2)}
              {currSymbol}
            </span>
          )}

          {+type === AssetType.Option &&
            (optionStrike || optionExpiration || optionTypeLabel) && (
              <span style={{ marginBottom: "-6px" }}>
                {[
                  optionTypeLabel,
                  optionStrike && `Strike: ${optionStrike}`,
                  optionExpiration && `Exp: ${optionExpiration}`,
                ]
                  .filter(Boolean)
                  .join(" | ")}
              </span>
            )}

          <div>
            <span
              style={{
                marginRight: change ? 4 : 0,
              }}
            >
              {t("Market price")}: <b>{getPriceFormatted()}</b>
            </span>
            {change && (
              <span className={change < 0 ? "loss" : "gain"}>
                {changePercent}%
              </span>
            )}
          </div>
        </div>
        <div className="deal-on-account">
          <span>{isOption ? t("premiumReceived") : t("mainAccount")}</span>
          <p>
            {isOption ? (
              getTotalPrice() ? (
                `${formatPrice(getTotalPrice(), 4)}${currSymbol}`
              ) : (
                "—"
              )
            ) : (
              <>
                {getUserAccount().amount}
                {getUserAccount().symbol}
              </>
            )}
          </p>
        </div>
        {onlyForLimitOrders && (
          <BargainingAlert
            variant="info"
            text={t("onlyLimitOrdersAvailable")}
          />
        )}
        {!onlyMarket && (
          <div
            onClick={() => {
              setModal(true);
            }}
            className="price-type-container"
          >
            <p>{t("orderType")}</p>
            <div>
              <p>{activeOrderType?.title ? t(activeOrderType.title) : ""}</p>
              <img src={ChevronDownLiner} alt="ChevronDownLiner" />
            </div>
          </div>
        )}
        {showTif && (
          <div
            onClick={() => {
              setTifModal(true);
            }}
            className="tif-type-container"
          >
            <p>{t("validityPeriod")}</p>

            <div>
              <p>{t(tifOptions[checkedTifIndex].title)}</p>
              <img src={ChevronDownLiner} alt="ChevronDownLiner" />
            </div>
          </div>
        )}

        {isBond ? (
          <div className="deal-price-yield-container">
            <div className="price-box-bond">
              <p
                style={{
                  fontSize:
                    i18n.language === "ru" || i18n.language === "am"
                      ? "12px"
                      : "14px",
                }}
              >
                {t("price")}:
              </p>
              {isSpecific ? (
                <>
                  <input
                    className="specific-price-input"
                    type="number"
                    value={specificPrice ?? ""}
                    onChange={(e) => {
                      const strValue = String(e.target.value);
                      const value = strValue === "" ? undefined : +strValue;
                      setSpecificPrice(value as number);
                      if (bondPriceCalcTimeoutRef.current) {
                        clearTimeout(bondPriceCalcTimeoutRef.current);
                      }
                      if (
                        bondIsin &&
                        value != null &&
                        !Number.isNaN(value) &&
                        value > 0
                      ) {
                        setIsBondPriceCalculating(true);
                        bondPriceCalcTimeoutRef.current = setTimeout(() => {
                          bondPriceCalcTimeoutRef.current = null;
                          setIsBondPriceCalculating(true);
                          calculateBondPrice({
                            isin: bondIsin,
                            price: value,
                            yld: null,
                          })
                            .then((result) => {
                              if (result?.yld != null)
                                setCalculatedBondYield(result.yld);
                              else setCalculatedBondYield(null);
                            })
                            .finally(() => setIsBondPriceCalculating(false));
                        }, 400);
                      } else {
                        setCalculatedBondYield(null);
                        setIsBondPriceCalculating(false);
                      }
                    }}
                    min={0}
                    step={0.01}
                    autoFocus={true}
                  />
                  <span>%</span>
                </>
              ) : (
                <span>{bid} %</span>
              )}
            </div>

            <img src={ArrowSwitch} alt="ArrowSwitch" />

            <div className="price-box-bond">
              <p
                style={{
                  fontSize:
                    i18n.language === "ru" || i18n.language === "am"
                      ? "12px"
                      : "14px",
                }}
              >
                {t("yield")}:
              </p>
              {isSpecific ? (
                <>
                  <input
                    className="specific-price-input"
                    type="text"
                    inputMode="decimal"
                    value={
                      yieldInputFocused
                        ? yieldInputValue
                        : bondYieldDisplay != null
                        ? String(bondYieldDisplay)
                        : ""
                    }
                    onFocus={() => {
                      setYieldInputFocused(true);
                      setYieldInputValue(
                        bondYieldDisplay != null
                          ? String(bondYieldDisplay)
                          : "",
                      );
                    }}
                    onBlur={() => setYieldInputFocused(false)}
                    onChange={(e) => {
                      const strValue = e.target.value.replace(",", ".");
                      setYieldInputValue(strValue);
                      const yieldPercent = +strValue;
                      if (
                        !Number.isNaN(yieldPercent) &&
                        bondIsin &&
                        yieldPercent >= 0
                      ) {
                        if (bondYieldCalcTimeoutRef.current) {
                          clearTimeout(bondYieldCalcTimeoutRef.current);
                        }
                        setIsBondPriceCalculating(true);
                        bondYieldCalcTimeoutRef.current = setTimeout(() => {
                          bondYieldCalcTimeoutRef.current = null;
                          calculateBondPrice({
                            isin: bondIsin,
                            price: null,
                            yld: yieldPercent,
                          })
                            .then((result) => {
                              if (result?.price != null) {
                                setSpecificPrice(result.price);
                                setCalculatedBondYield(yieldPercent);
                              }
                            })
                            .finally(() => setIsBondPriceCalculating(false));
                        }, 400);
                      } else {
                        setIsBondPriceCalculating(false);
                      }
                    }}
                    min={0}
                    step={0.01}
                    placeholder="—"
                  />
                  <span>%</span>
                </>
              ) : (
                <span>
                  {bondYieldDisplay != null ? `${bondYieldDisplay} %` : "—"}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div
            className={
              getDepthValidationAlert() || isWrongSpecificPrice
                ? "price-box price-box-validation-alert"
                : "price-box"
            }
            style={{
              marginBottom:
                getDepthValidationAlert() && isCurrency ? "40px" : undefined,
            }}
          >
            {isSpecific ? (
              <div className="price-box-specific-content">
                <p>{t("price")}</p>

                <div>
                  <input
                    className="specific-price-input"
                    type={"number"}
                    value={specificPrice}
                    onChange={(e) => {
                      // const newValue = e.target.value.replace(/[^0-9.]/g, "");
                      // setSpecificPrice(parseFloat(newValue));
                      const strValue = String(e.target.value);
                      const value = +strValue || "";
                      setSpecificPrice(value as number);
                    }}
                    min={0}
                    autoFocus={true}
                  />
                  <span>{currSymbol}</span>
                </div>
              </div>
            ) : (
              <div className="price-box-regular-content">
                <p>{t("price")}</p>
                <span>
                  {getAssetPrice()}
                  {currSymbol}
                </span>
              </div>
            )}

            {isWrongSpecificPrice && (
              <span className="depth-validation-alert">{t("wrongValue")}</span>
            )}

            {getDepthValidationAlert()}
          </div>
        )}

        {isBond ? (
          <div
            className={
              isWrongBondQuantity
                ? "deal-quantity deal-quantity-bond deal-quantity-validation-alert"
                : "deal-quantity deal-quantity-bond"
            }
          >
            <label htmlFor="quantity">
              {t("quantity")}
              <br />
              <p>
                {t("min")}:{" "}
                <b>
                  {getFromLotsText(getMinimumLots(), t)} ({lotSize} {t("unit")}){" "}
                </b>
              </p>
            </label>
            <QuantityInput
              assetType={type}
              inputValue={inputValue}
              setInputValue={setInputValue}
              setIsActive={setIsActive}
              placeholder={`${t("from")} ${getFromLotsText(
                getMinimumLots(),
                t,
              )}`}
              fromLots={getMinimumLots()}
              decimalPlaces={assetInfo?.decimal_places || undefined}
            />
            <div className="deal-quantity-steps">
              <button
                onClick={() => handleDecreaseQuantity()}
                disabled={inputValue <= getMinimumLots() || !inputValue}
              >
                <img src={MinusStep} alt="MinusStep" />
              </button>
              <button
                onClick={() => handleIncreaseQuantity()}
                disabled={!!inputValue && inputValue >= getAvailableLots()}
              >
                <img src={PlusStep} alt="PlusStep" />
              </button>
              <button
                onClick={() =>
                  setInputValue(
                    [AssetType.Currency].includes(assetInfo!.asset!.type)
                      ? +getAvailableLots().toFixed(4)
                      : getAvailableLots(),
                  )
                }
                disabled={
                  isOption ||
                  getAvailableLots() === 0 ||
                  inputValue >= getAvailableLots()
                }
              >
                <img src={Max} alt="Max" />
              </button>
            </div>

            {isWrongBondQuantityContent}
          </div>
        ) : (
          <div
            className={
              isWrongBondQuantity
                ? "deal-quantity deal-quantity-validation-alert"
                : "deal-quantity"
            }
          >
            <label htmlFor="quantity">
              {t("quantity")}
              <br />
              <p>
                {t("min")}:{" "}
                <b>
                  {getFromLotsText(getMinimumLots(), t)} ({lotSize} {t("unit")}){" "}
                </b>
              </p>
            </label>

            <QuantityInput
              assetType={type}
              inputValue={inputValue}
              setInputValue={setInputValue}
              setIsActive={setIsActive}
              fromLots={getMinimumLots()}
              decimalPlaces={assetInfo?.decimal_places || undefined}
            />

            <div className="deal-quantity-steps">
              <button
                onClick={() => handleDecreaseQuantity()}
                disabled={inputValue <= getMinimumLots() || !inputValue}
              >
                <img src={MinusStep} alt="MinusStep" />
              </button>
              <button
                onClick={() => handleIncreaseQuantity()}
                disabled={!!inputValue && inputValue >= getAvailableLots()}
              >
                <img src={PlusStep} alt="PlusStep" />
              </button>
              <button
                onClick={() =>
                  setInputValue(
                    [AssetType.Currency].includes(assetInfo!.asset!.type)
                      ? +getAvailableLots().toFixed(4)
                      : getAvailableLots(),
                  )
                }
                disabled={
                  isOption ||
                  getAvailableLots() === 0 ||
                  inputValue >= getAvailableLots()
                }
              >
                <img src={Max} alt="Max" />
              </button>
            </div>

            {isWrongBondQuantityContent}
          </div>
        )}

        {getTotalPriceAndCommission() && (
          <div
            className="deal-commission"
            onClick={() => setCommissionExpanded(!commissionExpanded)}
          >
            <div className="deal-commission-content">
              <p>
                {isOption ? t("premiumReceived") : t("total_value")}:{" "}
                <b>{getTotalPriceAndCommission()}</b>
              </p>
              <img
                style={{
                  transform: commissionExpanded
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                }}
                src={ChevronDownLiner}
                alt="ChevronDownLiner"
              />
            </div>

            {commissionExpanded && (
              <ul className="deal-commission-expanded-content">
                {isBond ? (
                  <li>
                    {t("order_value")}:{" "}
                    <b>
                      {formatPrice(getTotalPrice(false, false, true), 4)}
                      {currSymbol}
                    </b>
                  </li>
                ) : (
                  <li>
                    {t("order_value")}:{" "}
                    <b>
                      {formatPrice(getTotalPrice())}
                      {currSymbol}
                    </b>
                  </li>
                )}

                {isBond &&
                  bondInfo &&
                  qty &&
                  bondInfo.coupon_type === "fixed" && (
                    <li>
                      {t("ACI_full")}:{" "}
                      <b>
                        {+((qty || 1) * bondInfo.ai).toFixed(4)}
                        {currSymbol}
                      </b>
                    </li>
                  )}

                {getAssetCommission()}
              </ul>
            )}
          </div>
        )}

        {isCurrency && (
          <div className="currencies-nav-page-danger-box">
            <span>{t("fxLimitText")}</span>
          </div>
        )}
      </div>

      <div className="btn-wrapper deal-btn-wrapper">
        <Button
          type="submit"
          disabled={
            unavailable() ||
            isLoading ||
            isBondPriceCalculating ||
            !isExchangeActive ||
            getDepthValidationAlert() ||
            (isSpecific && +(specificPrice || 0) <= 0)
          }
          variant="pink"
          className="customBtn deal-btn"
          // onClick={handleSell}
          onClick={() =>
            action(() => setConfirmDeal(true), isCurrency ? "fx" : undefined)
          }
        >
          {t("place_order")}
          <span>{getTotalPriceAndCommission()}</span>
        </Button>
      </div>

      {modal && (
        <PriceTypeModal
          onClose={() => setModal(false)}
          checked={checkedIndex}
          setChecked={(i: number) => {
            setCheckedIndex(i);
            if (orderTypes[i].showInput && !specificPrice && (bid || last)) {
              setSpecificPrice(bid || last);
            }
          }}
          list={orderTypes}
        />
      )}

      {showTif && tifModal && (
        <PriceTypeModal
          onClose={() => setTifModal(false)}
          checked={checkedTifIndex}
          setChecked={(i: number) => {
            setCheckedTifIndex(i);
            setTif(availableOrderTifs[i]);
          }}
          list={tifOptions}
        />
      )}

      {confirmDeal === true && (
        <Modal
          customModal="modalLogOut"
          onClick={handleSell}
          onHide={() => setConfirmDeal(false)}
          title={t("confirmDeal")}
          text={t("toCompleteDeal")}
          btnText={t("confirm")}
          showSecondBtn={false}
          customOutlet="customOutlet"
          confirmDeal={true}
          isLoading={load}
          dealError={dealError}
        />
      )}
    </div>
  );
};
